import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import axios from 'axios';
import { ApiErrorType, AsCaaksError, CaaksError } from 'src/app/shared/error/errors';
import { ErrorState } from 'src/app/shared/error/store/states/error.state';
import { ErrorService } from 'src/app/shared/error/services/error.service';
import { RumIntegrationService } from 'src/app/services/rum-integration.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private readonly ngZone_: NgZone,
    private readonly injector_: Injector,
    private readonly errorService_: ErrorService,
    private rumIntegrationService: RumIntegrationService,
  ) {}

  private get router_(): Router {
    return this.injector_.get(Router);
  }

  handleError(error: any): void {
    // NG0100 (ExpressionChangedAfterItHasBeenChecked) are development-mode-only.
    // They never occur in production builds, so skip RUM reporting and error navigation.
    if (this.isNg0100Error_(error)) {
      console.warn(
        '[sbvp_gui][globalErrorHandler] NG0100 ignoré (développement uniquement) :',
        this.unwrapError_(error)?.message ?? error,
      );
      return;
    }

    this.logDiagnostic_(error);

    if (!(error instanceof HttpErrorResponse)) {
      this.rumIntegrationService.reportJsError(error);
    }

    // Convertir une erreur axios en HttpErrorResponse
    if (axios.isAxiosError(error)) {
      error = new HttpErrorResponse({
        error: error.response?.statusText,
        headers: error.request?.headers,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.request,
      });
    }

    // Si c'est une erreur HTTP, la gérer (reporting déjà fait par HttpInterceptor)
    if (error instanceof HttpErrorResponse) {
      console.log(
        '%c[sbvp_gui][globalErrorHandler] HttpError détectée (déjà reportée par HttpInterceptor)',
        'color: green;',
        error,
      );

      const handled = this.handleHTTPError_(error);
      if (handled) return;
    }

    if (error instanceof CaaksError) {
      console.log('%c[sbvp_gui][globalErrorHandler] CaaksError détectée', 'color: purple;', error);
      this.rumIntegrationService.reportJsError(error);
      this.errorService_.handleError(error);
      return;
    }

    // Ne pas re-throw : cela créerait une boucle infinie via Zone.js
    // (Zone.js → handleError → throw → Zone.js → handleError → ...)
    console.error('[sbvp_gui][globalErrorHandler] Erreur non gérée :', error);
  }

  private logDiagnostic_(error: any): void {
    try {
      const candidate = this.unwrapError_(error);
      const xhrTarget = this.extractXhrTarget_(candidate);
      const diagnostic = {
        route: this.safeRoute_(),
        location: typeof window !== 'undefined' ? window.location.href : '',
        rawType: error?.constructor?.name ?? typeof error,
        candidateType: candidate?.constructor?.name ?? typeof candidate,
        name: candidate?.name ?? error?.name ?? null,
        message: candidate?.message ?? error?.message ?? null,
        status: candidate?.status ?? null,
        statusText: candidate?.statusText ?? null,
        url: candidate?.url ?? xhrTarget?.responseURL ?? null,
        readyState: xhrTarget?.readyState ?? null,
        responseType: xhrTarget?.responseType ?? null,
        keys: candidate && typeof candidate === 'object' ? Object.keys(candidate).slice(0, 20) : [],
      };

      console.error(
        '[sbvp_gui][globalErrorHandler] Diagnostic snapshot:',
        JSON.stringify(diagnostic),
      );

      console.groupCollapsed('[sbvp_gui][globalErrorHandler] Diagnostic');
      console.log('diagnostic', diagnostic);
      console.log('raw error', error);

      if (candidate !== error) {
        console.log('unwrapped error', candidate);
      }

      const stack = candidate?.stack ?? error?.stack;
      if (typeof stack === 'string' && stack.length > 0) {
        console.log('stack', stack);
      }

      console.groupEnd();
    } catch (diagnosticError) {
      console.warn(
        '[sbvp_gui][globalErrorHandler] Impossible de produire le diagnostic',
        diagnosticError,
      );
    }
  }

  private isNg0100Error_(error: any): boolean {
    const msg: string = this.unwrapError_(error)?.message ?? error?.message ?? '';
    return typeof msg === 'string' && msg.includes('NG0100');
  }

  private unwrapError_(error: any): any {
    return error?.rejection ?? error?.ngOriginalError ?? error?.originalError ?? error;
  }

  private extractXhrTarget_(error: any): XMLHttpRequest | null {
    const target = error?.target ?? error?.currentTarget ?? error?.error?.target ?? null;
    if (target && typeof target === 'object' && 'readyState' in target && 'responseURL' in target) {
      return target as XMLHttpRequest;
    }
    return null;
  }

  private safeRoute_(): string {
    try {
      return this.router_.url;
    } catch {
      return '';
    }
  }

  /**
   * Gère les erreurs HTTP spécifiques avec redirection
   * Retourne true si l'erreur est gérée ici
   */
  private handleHTTPError_(error: HttpErrorResponse): boolean {
    let isHandled = false;

    this.ngZone_.run(() => {
      const caaksError = AsCaaksError(error);

      // Vérifier si l'erreur a déjà été gérée par l'intercepteur HTTP (401 ou 403)
      if ((error as any)._handledByInterceptor) {
        isHandled = true;
        return;
      }

      if (CaaksError.isError(error, ApiErrorType.tokenExpired, ApiErrorType.encryptionKeyChanged)) {
        this.navigateToErrorPage_('home', caaksError);
        isHandled = true;
        return;
      }

      // Vérifier si l'erreur 403 (Forbidden)
      if (error.status === 403 || CaaksError.isError(error, ApiErrorType.forbidden)) {
        this.navigateToErrorPage_('not-authorized', caaksError);
        isHandled = true;
        return;
      }

      if (!this.router_.routerState.snapshot.url.includes('error')) {
        this.navigateToErrorPage_('error', caaksError, true);
        isHandled = true;
      }
    });

    return isHandled;
  }

  /**
   * Redirige vers la page d'erreur souhaitée
   */
  private navigateToErrorPage_(
    route: string,
    error: CaaksError,
    preserveQueryParams: boolean = false,
  ): void {
    this.router_.navigate([route], {
      state: { error } as ErrorState,
      queryParamsHandling: preserveQueryParams ? 'preserve' : undefined,
    });
  }
}
